import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  User,
  Briefcase,
  FileText,
  Sparkles,
  Zap,
  Award,
  Filter,
  CheckCircle2
} from 'lucide-react';
import { ClientTimelineEvent, ClientTimelineEventType } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { formatDate } from '../../utils/dateUtils';

interface ClientTimelineProps {
  events: ClientTimelineEvent[];
  lang: Language;
  onOpenCase?: (caseId: string) => void;
  onOpenReport?: (reportId: string) => void;
}

const EVENT_TYPE_CONFIG: Record<
  ClientTimelineEventType,
  { labelEn: string; labelHi: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  INTAKE: { labelEn: 'Intake & Calculation', labelHi: 'प्रारंभिक कुंडली गणना', icon: User, color: 'bg-blue-500 text-white' },
  BIRTH_UPDATE: { labelEn: 'Birth Details Modified', labelHi: 'जन्म विवरण संशोधन', icon: Clock, color: 'bg-astro-secondary text-white' },
  CASE_CREATED: { labelEn: 'Case Opened', labelHi: 'प्रकरण प्रारंभ', icon: Briefcase, color: 'bg-astro-secondary-surface0 text-white' },
  CONSULTATION: { labelEn: 'Consultation Session', labelHi: 'परामर्श सत्र', icon: Award, color: 'bg-orange-500 text-white' },
  QUESTION: { labelEn: 'Client Question', labelHi: 'जिज्ञासा / प्रश्न', icon: Sparkles, color: 'bg-purple-500 text-white' },
  PRACTITIONER_NOTE: { labelEn: 'Practitioner Note', labelHi: 'आचार्य अभिलेख', icon: FileText, color: 'bg-astro-secondary text-white' },
  REPORT_GENERATED: { labelEn: 'Report Generated', labelHi: 'रिपोर्ट संकलन', icon: FileText, color: 'bg-indigo-500 text-white' },
  REMEDY_PRESCRIBED: { labelEn: 'Remedy Prescribed', labelHi: 'उपाय संस्तुति', icon: Sparkles, color: 'bg-emerald-500 text-white' },
  TIMING_MILESTONE: { labelEn: 'Timing Milestone', labelHi: 'समय काल मील का पत्थर', icon: Zap, color: 'bg-astro-secondary text-white' },
  FOLLOW_UP: { labelEn: 'Follow-Up Complete', labelHi: 'फॉलो-अप संपन्न', icon: CheckCircle2, color: 'bg-teal-500 text-white' }
};

export const ClientTimeline: React.FC<ClientTimelineProps> = ({
  events,
  lang,
  onOpenCase,
  onOpenReport
}) => {
  const isHi = lang === 'hi';
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('ALL');

  const filteredEvents = selectedTypeFilter === 'ALL'
    ? events
    : events.filter((e) => e.type === selectedTypeFilter);

  return (
    <div className="space-y-4">
      {/* Timeline Filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-astro-secondary-surface dark:bg-astro-primary/60 p-3 rounded-xl border border-astro-border/80 dark:border-astro-strong-border">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-astro-muted" />
          <span className="text-xs font-bold text-astro-secondary-text dark:text-astro-muted">
            {isHi ? 'टाइमलाइन फ़िल्टर:' : 'Filter Timeline:'}
          </span>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedTypeFilter('ALL')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
              selectedTypeFilter === 'ALL'
                ? 'bg-astro-secondary-surface0 text-white'
                : 'bg-white dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted border border-astro-border dark:border-astro-strong-border'
            }`}
          >
            {isHi ? 'सभी घटनाएं' : 'All Events'} ({events.length})
          </button>

          {['CASE_CREATED', 'CONSULTATION', 'REPORT_GENERATED', 'FOLLOW_UP'].map((tKey) => {
            const conf = EVENT_TYPE_CONFIG[tKey as ClientTimelineEventType];
            const count = events.filter((e) => e.type === tKey).length;
            if (count === 0) return null;
            return (
              <button
                key={tKey}
                onClick={() => setSelectedTypeFilter(tKey)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                  selectedTypeFilter === tKey
                    ? 'bg-astro-secondary-surface0 text-white'
                    : 'bg-white dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted border border-astro-border dark:border-astro-strong-border'
                }`}
              >
                {isHi ? conf.labelHi : conf.labelEn} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline Stream */}
      {filteredEvents.length > 0 ? (
        <div className="relative pl-6 border-l-2 border-astro-border dark:border-astro-strong-border/80 space-y-6 my-2">
          {filteredEvents.map((ev) => {
            const conf = EVENT_TYPE_CONFIG[ev.type] || EVENT_TYPE_CONFIG.INTAKE;
            const Icon = conf.icon;
            const dateStr = formatDate(ev.timestamp);

            return (
              <div key={ev.id} className="relative group">
                {/* Timeline node icon */}
                <div
                  className={`absolute -left-[35px] top-0 p-1.5 rounded-full ${conf.color} shadow-xs`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>

                {/* Event Card */}
                <div className="bg-white dark:bg-slate-850 p-3.5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-1.5 hover:border-amber-400/40 transition-colors">
                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-astro-text dark:text-astro-muted">{ev.title}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                        {isHi ? conf.labelHi : conf.labelEn}
                      </span>
                    </div>
                    <span className="text-[11px] text-astro-muted dark:text-astro-muted font-mono">
                      {dateStr}
                    </span>
                  </div>

                  <p className="text-xs text-astro-secondary-text dark:text-astro-muted leading-relaxed">
                    {ev.description}
                  </p>

                  <div className="flex items-center justify-between text-[11px] text-astro-muted dark:text-astro-muted pt-1 border-t border-astro-border dark:border-astro-strong-border/80">
                    <span>Practitioner: {ev.practitioner}</span>
                    <div className="flex items-center gap-2">
                      {ev.relatedCaseId && onOpenCase && (
                        <button
                          onClick={() => onOpenCase(ev.relatedCaseId!)}
                          className="font-bold text-amber-600 hover:underline cursor-pointer"
                        >
                          View Case
                        </button>
                      )}
                      {ev.relatedReportId && onOpenReport && (
                        <button
                          onClick={() => onOpenReport(ev.relatedReportId!)}
                          className="font-bold text-indigo-600 hover:underline cursor-pointer"
                        >
                          View Report
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-8 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 text-astro-muted text-xs">
          {isHi ? 'इस फ़िल्टर के लिए कोई घटना दर्ज नहीं है।' : 'No timeline events found for this filter.'}
        </div>
      )}
    </div>
  );
};
